/**
 * Booking Form Block
 *
 * Step 1 → choose product(s)
 * Step 2 → pick date range (calendar, blocked dates from REST)
 * Step 3 → customer details
 * Submit → POST /bp/v1/bookings
 */

const MONTHS = [
    'Sausis','Vasaris','Kovas','Balandis','Gegužė','Birželis',
    'Liepa','Rugpjūtis','Rugsėjis','Spalis','Lapkritis','Gruodis',
];
const WEEK_DAYS = ['P','A','T','K','Pn','Š','S']; // Mon-first Lithuanian

const ADDON_PRICES = {
    extension_cable: 20,
    generator:       100,
};

// ── Utilities ─────────────────────────────────────────────────────────────────

function toStr(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function todayStr() { return toStr(new Date()); }

/**
 * Site "today" (Y-m-d) from the form markup, falling back to the visitor's local date.
 * Same-day bookings are not allowed — the earliest selectable day is tomorrow.
 */
function siteTodayStr(root) {
    return root?.dataset?.today || todayStr();
}

function parseDate(str) {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
}

function displayDate(str) {
    const [y, m, d] = str.split('-');
    return `${d}.${m}.${y}`;
}

function make(tag, className = '', html = '') {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (html)      el.innerHTML = html;
    return el;
}

/**
 * Escapes a value for safe use inside innerHTML template literals.
 * Converts the value to a plain text node and reads back the HTML-encoded form,
 * so characters like <, >, ", & are entity-encoded before injection.
 */
function sanitizeText(value) {
    const node = document.createTextNode(String(value ?? ''));
    const wrapper = document.createElement('span');
    wrapper.appendChild(node);
    return wrapper.innerHTML;
}

const FORM_MESSAGES = {
    required: 'Šis laukas yra privalomas.',
    email:    'Įveskite teisingą el. pašto adresą.',
    phone:    'Įveskite teisingą telefono numerį.',
    consent:  'Turite sutikti su privatumo politika.',
};

function getFormFieldWrapper(el) {
    return el?.closest('.booking-form__field') ?? null;
}

function clearFormFieldError(wrapper) {
    if (!wrapper) return;
    wrapper.classList.remove('is-invalid');
    wrapper.querySelector('.booking-form__field-error')?.remove();
    wrapper.querySelectorAll('.is-invalid').forEach((el) => el.classList.remove('is-invalid'));
}

function setFormFieldError(wrapper, message) {
    if (!wrapper) return;
    wrapper.classList.add('is-invalid');

    let err = wrapper.querySelector('.booking-form__field-error');
    if (!err) {
        err = make('span', 'booking-form__field-error');
        err.setAttribute('role', 'alert');
        wrapper.appendChild(err);
    }
    err.textContent = message;

    const input = wrapper.querySelector('.booking-form__input, textarea, input[type="checkbox"]');
    input?.classList.add('is-invalid');
}

function clearCustomerFormErrors(form) {
    form.querySelectorAll('.booking-form__field').forEach(clearFormFieldError);
}

function validateCustomerForm(form) {
    clearCustomerFormErrors(form);

    let valid = true;
    let firstInvalid = null;

    const fail = (el, message) => {
        setFormFieldError(getFormFieldWrapper(el), message);
        valid = false;
        if (!firstInvalid) firstInvalid = el;
    };

    const name = form.querySelector('[name="customer_name"]');
    if (!name.value.trim()) fail(name, FORM_MESSAGES.required);

    const phone = form.querySelector('[name="customer_phone"]');
    if (!phone.value.trim()) {
        fail(phone, FORM_MESSAGES.required);
    } else if (phone.value.replace(/\D/g, '').length < 8) {
        fail(phone, FORM_MESSAGES.phone);
    }

    const email = form.querySelector('[name="customer_email"]');
    const emailVal = email.value.trim();
    if (!emailVal) {
        fail(email, FORM_MESSAGES.required);
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
        fail(email, FORM_MESSAGES.email);
    }

    const address = form.querySelector('[name="event_address"]');
    if (!address.value.trim()) fail(address, FORM_MESSAGES.required);

    const consent = form.querySelector('[name="consent"]');
    if (!consent.checked) fail(consent, FORM_MESSAGES.consent);

    if (form.querySelector('#bf-invoice')?.checked) {
        ['invoice_company_name', 'invoice_company_code', 'invoice_company_address'].forEach((fieldName) => {
            const input = form.querySelector(`[name="${fieldName}"]`);
            if (input && !input.value.trim()) fail(input, FORM_MESSAGES.required);
        });
    }

    return { valid, firstInvalid };
}

function bindCustomerFormValidation(form) {
    form.querySelectorAll('input, textarea').forEach((input) => {
        const clear = () => {
            const wrapper = getFormFieldWrapper(input);
            if (wrapper?.classList.contains('is-invalid')) {
                clearFormFieldError(wrapper);
            }
        };
        input.addEventListener('input', clear);
        input.addEventListener('change', clear);
    });
}

function countBookingDays(from, to) {
    const d1 = parseDate(from);
    const d2 = parseDate(to);
    return Math.max(1, Math.round((d2 - d1) / 86400000) + 1);
}

function formatEuro(amount) {
    return `€${Math.round(amount).toLocaleString('lt-LT')}`;
}

/** Returns true if date is a weekend day: Fri (5), Sat (6), Sun (0). */
function isWeekendDay(date) {
    const dow = date.getDay(); // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat
    return dow === 0 || dow === 5 || dow === 6;
}

function calculateBookingPricing(products, selectedIds, dateFrom, dateTo, addons) {
    let productsTotal = 0;
    let weekdayDays   = 0;
    let weekendDays   = 0;

    const d1 = parseDate(dateFrom);
    const d2 = parseDate(dateTo);

    // Iterate each day individually and apply weekday vs weekend price.
    const cur = new Date(d1);
    while (cur <= d2) {
        const weekend = isWeekendDay(cur);
        if (weekend) { weekendDays++; } else { weekdayDays++; }

        products
            .filter(p => selectedIds.has(p.id))
            .forEach(p => {
                const dayPrice = weekend
                    ? (p.price_weekend || p.price_weekday || p.price || 0)
                    : (p.price_weekday || p.price || 0);
                productsTotal += dayPrice;
            });

        cur.setDate(cur.getDate() + 1);
    }

    let addonsTotal = 0;
    if (addons.extensionCable) addonsTotal += ADDON_PRICES.extension_cable;
    if (addons.generator)      addonsTotal += ADDON_PRICES.generator;

    return {
        days: weekdayDays + weekendDays,
        weekdayDays,
        weekendDays,
        productsTotal,
        addonsTotal,
        total: productsTotal + addonsTotal,
    };
}

function formatDateRange(from, to) {
    if (!from || !to) return '';
    return from === to
        ? displayDate(from)
        : `${displayDate(from)} – ${displayDate(to)}`;
}

// ── API ───────────────────────────────────────────────────────────────────────

function resolveBookingApiBase(root) {
    const fromAttr = (root.getAttribute('data-api-url') || root.dataset.apiUrl || '').replace(/\/$/, '');

    if (fromAttr && fromAttr !== 'undefined') {
        return fromAttr;
    }

    return `${window.location.origin}/wp-json/bp/v1`;
}

async function apiFetch(url, opts = {}) {
    const res  = await fetch(url, opts);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || json.message || 'API error');
    return json;
}

// ── Init ──────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.booking-form').forEach(init);
});

async function init(root) {
    const apiBase    = resolveBookingApiBase(root);
    const nonce      = root.dataset.nonce;
    const successMsg = root.dataset.successMessage || 'Ačiū! Rezervacija gauta.';
    const privacyUrl = root.dataset.privacyUrl     || '/privatumo-politika';
    const siteToday  = siteTodayStr(root);
    const body     = root.querySelector('.booking-form__body');
    const stepsEl  = root.querySelector('.booking-form__steps');

    let calendarToastTimer = null;
    let calFloatBar        = null;

    const siteTodayDate = parseDate(siteToday);

    const state = {
        step:         1,
        products:     [],
        selected:     new Set(),   // product IDs
        blockedDates: new Set(),   // Y-m-d strings
        dateFrom:     null,
        dateTo:       null,
        calYear:      siteTodayDate.getFullYear(),
        calMonth:     siteTodayDate.getMonth(),
    };

    // Initial load
    showLoading(body);

    try {
        state.products = await apiFetch(`${apiBase}/products`);
    } catch {
        body.innerHTML = '<p class="booking-form__error-msg">Nepavyko įkelti produktų. Perkraukite puslapį.</p>';
        return;
    }

    // ── Navigation ────────────────────────────────────────────────────────────

    function goToStep(step) {
        teardownStickyNav();
        teardownCalendarFloat();
        dismissCalendarToast();
        state.step = step;
        updateStepIndicator();
        body.classList.add('is-transitioning');
        setTimeout(() => {
            if      (step === 1) renderProducts();
            else if (step === 2) renderCalendar();
            else if (step === 3) renderCustomer();
            body.classList.remove('is-transitioning');
        }, 150);
    }

    function updateStepIndicator() {
        stepsEl.querySelectorAll('.booking-form__step-item').forEach((item, i) => {
            const n = i + 1;
            item.classList.toggle('is-active',   n === state.step);
            item.classList.toggle('is-complete',  n < state.step);
        });
    }

    // ── Loading helper ────────────────────────────────────────────────────────

    function showLoading(container) {
        container.innerHTML =
            '<div class="booking-form__loading"><span></span><span></span><span></span></div>';
    }

    // ── Nav bar builder ───────────────────────────────────────────────────────

    function makeNav({ back = false, nextLabel = 'Toliau', nextLocked = false }) {
        const nav = make('div', 'booking-form__nav');

        if (back) {
            const backBtn = make('button', 'btn btn--secondary booking-form__btn-back', '← Atgal');
            backBtn.type = 'button';
            nav.appendChild(backBtn);
        }

        const nextBtn = make('button', 'btn btn--primary booking-form__btn-next', nextLabel);
        nextBtn.type = 'button';
        if (nextLocked) nextBtn.classList.add('is-locked');
        nav.appendChild(nextBtn);

        return nav;
    }

    // ── UX helpers ────────────────────────────────────────────────────────────

    function lockBtn(btn, locked) {
        btn.classList.toggle('is-locked', locked);
    }

    function shakeBtn(btn) {
        btn.classList.remove('is-shaking');
        void btn.offsetWidth; // force reflow to restart animation
        btn.classList.add('is-shaking');
        btn.addEventListener('animationend', () => btn.classList.remove('is-shaking'), { once: true });
    }

    function flashBtnHint(buttons, hint, revertLabel, duration = 2400) {
        buttons.forEach((btn) => {
            btn.textContent = hint;
            btn.classList.add('is-hint');
        });

        clearTimeout(flashBtnHint._t);
        flashBtnHint._t = setTimeout(() => {
            buttons.forEach((btn) => {
                btn.classList.remove('is-hint');
                if (btn.dataset.defaultLabel) {
                    btn.textContent = btn.dataset.defaultLabel;
                }
            });
        }, duration);
    }

    function showNavError(nav, message) {
        let err = nav.querySelector('.booking-form__nav-error');
        if (!err) {
            err = make('div', 'booking-form__nav-error');
            nav.appendChild(err);
        }
        err.textContent = message;
        clearTimeout(err._t);
        err._t = setTimeout(() => err.remove(), 3500);
    }

    // ── Sticky nav (step 1 — legacy hook, kept for cleanup) ───────────────────

    function teardownStickyNav() {
        root._stickyNavObserver?.disconnect();
        root._stickyNavObserver = null;
        root.classList.remove('is-step-products');
    }

    // ── Sticky nav + toast (step 2 — calendar) ──────────────────────────────

    function teardownCalendarFloat() {
        root._calendarFloatObserver?.disconnect();
        root._calendarFloatObserver = null;
        root.classList.remove('is-step-calendar', 'is-calendar-ready');
        if (calFloatBar) {
            calFloatBar.remove();
            calFloatBar = null;
        }
    }

    /**
     * IntersectionObserver on the docked nav: when docked nav scrolls off-screen
     * and a date is selected, show the float bar; when it comes back, hide it.
     */
    function setupCalendarFloat(dockedNav) {
        if (!window.matchMedia('(max-width: 768px)').matches) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!calFloatBar) return;
                if (entry.isIntersecting) {
                    calFloatBar.classList.remove('is-visible');
                } else if (root.classList.contains('is-calendar-ready')) {
                    calFloatBar.classList.add('is-visible');
                }
            },
            { threshold: 0.01 }
        );

        observer.observe(dockedNav);
        root._calendarFloatObserver = observer;
    }

    /**
     * Show or hide float bar based on whether a date is selected
     * and whether the docked nav is currently off-screen.
     */
    function syncCalendarFloat(hasSelection) {
        root.classList.toggle('is-calendar-ready', hasSelection);

        if (!window.matchMedia('(max-width: 768px)').matches || !calFloatBar) return;

        if (!hasSelection) {
            calFloatBar.classList.remove('is-visible');
            return;
        }

        const dockedNav = body.querySelector('.booking-form__nav');
        if (!dockedNav) return;

        const rect  = dockedNav.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;

        if (!inView) {
            calFloatBar.classList.add('is-visible');
            // After transition ends, scroll so selected date isn't covered
            setTimeout(scrollSelectedDayIntoView, 360);
        }
    }

    /** Scroll selected date cell into view above the float bar. */
    function scrollSelectedDayIntoView() {
        if (!calFloatBar || !state.dateFrom) return;
        const floatH = calFloatBar.offsetHeight + 16;
        const sel = body.querySelector(
            '.booking-form__day.is-from, .booking-form__day.is-to, .booking-form__day.is-selected'
        );
        if (!sel) return;
        const rect  = sel.getBoundingClientRect();
        const limit = window.innerHeight - floatH;
        if (rect.bottom > limit) {
            window.scrollBy({ top: rect.bottom - limit, behavior: 'smooth' });
        }
    }

    function dismissCalendarToast() {
        clearTimeout(calendarToastTimer);
        const toast = root.querySelector('.booking-form__toast');
        if (!toast) {
            return;
        }
        toast.classList.remove('is-visible');
        toast.classList.add('is-hiding');
    }

    function showCalendarToast(message) {
        if (!window.matchMedia('(max-width: 768px)').matches) {
            return false;
        }

        let toast = root.querySelector('.booking-form__toast');
        if (!toast) {
            toast = make('div', 'booking-form__toast');
            toast.setAttribute('role', 'alert');
            toast.setAttribute('aria-live', 'assertive');
            root.appendChild(toast);
        }

        clearTimeout(calendarToastTimer);
        toast.textContent = message;
        toast.classList.remove('is-hiding');
        requestAnimationFrame(() => toast.classList.add('is-visible'));

        calendarToastTimer = setTimeout(() => {
            toast.classList.remove('is-visible');
            toast.classList.add('is-hiding');
        }, 4800);

        return true;
    }

    function setupStickyNav(footer, floatNav, dockedNav) {
        root.classList.add('is-step-products');

        const anchor = footer.querySelector('.booking-form__nav-anchor');
        const observer = new IntersectionObserver(
            ([entry]) => {
                const isDocked = entry.isIntersecting;
                footer.classList.toggle('is-docked', isDocked);
                floatNav.setAttribute('aria-hidden', isDocked ? 'true' : 'false');
                dockedNav.setAttribute('aria-hidden', isDocked ? 'false' : 'true');
            },
            { threshold: 0, rootMargin: '0px 0px -8px 0px' }
        );

        observer.observe(anchor);
        root._stickyNavObserver = observer;
    }

    // ── Step 1: Products ──────────────────────────────────────────────────────

    function renderProducts() {
        body.innerHTML = '';

        const grid = make('div', 'booking-form__product-grid');
        grid.dataset.count = String(state.products.length);

        let isAdvancing = false;

        state.products.forEach(product => {
            const isSelected = state.selected.has(product.id);
            const card = make(
                'div',
                `booking-form__product-card${isSelected ? ' is-selected' : ''}`
            );
            card.dataset.id = product.id;
            card.setAttribute('role', 'button');
            card.setAttribute('tabindex', '0');
            card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
            card.setAttribute('aria-label', product.name);

            const imgHtml = product.thumbnail
                ? `<div class="booking-form__product-media">
                     <img src="${product.thumbnail}" alt="" loading="lazy">
                     <span class="booking-form__product-selected" aria-hidden="true"></span>
                   </div>`
                : `<div class="booking-form__product-media booking-form__product-media--placeholder">
                     <span class="booking-form__product-selected" aria-hidden="true"></span>
                   </div>`;

            const bundleBadge = product.is_bundle
                ? `<span class="booking-form__product-badge">Komplektas</span>`
                : '';

            const capacity = product.capacity
                ? `<p class="booking-form__product-capacity">Iki ${sanitizeText(product.capacity)} vaikų</p>`
                : '';

            const desc = product.short_description
                ? `<p class="booking-form__product-desc">${sanitizeText(product.short_description)}</p>`
                : '';

            card.innerHTML = `
                ${imgHtml}
                <div class="booking-form__product-body">
                    <div class="booking-form__product-top">
                        <h3 class="booking-form__product-name">${sanitizeText(product.name)}</h3>
                        ${bundleBadge}
                    </div>
                    ${desc}
                    ${capacity}
                    <div class="booking-form__product-price">
                        <span class="booking-form__product-price-from">nuo</span>
                        <span class="booking-form__product-price-amount">€${sanitizeText(Math.round(product.price))}</span>
                        <span class="booking-form__product-price-unit">${sanitizeText(product.price_unit)}</span>
                    </div>
                </div>
            `;

            const selectAndAdvance = async () => {
                if (isAdvancing) return;

                state.selected.clear();
                state.selected.add(product.id);

                grid.querySelectorAll('.booking-form__product-card').forEach((item) => {
                    const id = parseInt(item.dataset.id, 10);
                    const selected = state.selected.has(id);
                    item.classList.toggle('is-selected', selected);
                    item.setAttribute('aria-pressed', selected ? 'true' : 'false');
                });

                isAdvancing = true;
                grid.classList.add('is-advancing');
                card.classList.add('is-advancing');

                try {
                    await loadAvailability();
                    goToStep(2);
                } catch {
                    isAdvancing = false;
                    grid.classList.remove('is-advancing');
                    card.classList.remove('is-advancing');
                }
            };

            card.addEventListener('click', selectAndAdvance);
            card.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectAndAdvance();
                }
            });

            grid.appendChild(card);
        });

        body.appendChild(grid);
    }

    async function loadAvailability() {
        const blocked = new Set();
        for (const id of state.selected) {
            try {
                const data = await apiFetch(`${apiBase}/availability?product_id=${id}&months=12`);
                data.blocked_dates.forEach(d => blocked.add(d));
            } catch { /* server re-validates on submit anyway */ }
        }
        state.blockedDates = blocked;
    }

    // ── Step 2: Calendar ──────────────────────────────────────────────────────

    function renderCalendar() {
        body.innerHTML = '';

        const infoEl = make('div', 'booking-form__calendar-info');
        refreshInfo(infoEl);

        const calEl = make('div', 'booking-form__calendar');
        buildMonthGrids(calEl);

        const hasSelection = !!(state.dateFrom && state.dateTo);

        // ── Docked nav: Atgal + Toliau — always in document flow ──────────────
        const dockedNav    = makeNav({ back: true, nextLabel: 'Toliau', nextLocked: !hasSelection });
        const dockedNextBtn = dockedNav.querySelector('.booking-form__btn-next');
        const NEXT_LABEL   = 'Toliau';
        const NEXT_HINT    = 'Pasirinkite datą';
        dockedNextBtn.dataset.defaultLabel = NEXT_LABEL;

        dockedNav.querySelector('.booking-form__btn-back').addEventListener('click', () => goToStep(1));
        dockedNextBtn.addEventListener('click', () => {
            if (dockedNextBtn.classList.contains('is-locked')) {
                shakeBtn(dockedNextBtn);
                flashBtnHint([dockedNextBtn], NEXT_HINT, NEXT_LABEL);
                return;
            }
            goToStep(3);
        });

        // ── Mobile float bar: Toliau only — fixed bottom, off-screen by default ─
        calFloatBar = make('div', 'booking-form__cal-float-bar');
        const floatBtn = make('button', 'btn btn--primary', 'Toliau');
        floatBtn.type = 'button';
        calFloatBar.appendChild(floatBtn);
        floatBtn.addEventListener('click', () => {
            if (dockedNextBtn.classList.contains('is-locked')) return;
            goToStep(3);
        });
        root.appendChild(calFloatBar);

        // ── Day click (event delegation) ──────────────────────────────────────
        calEl.addEventListener('click', e => {
            const day = e.target.closest('.booking-form__day');
            if (day && !day.disabled) {
                handleDayClick(day.dataset.date, calEl, infoEl, dockedNextBtn);
            }
            const navBtn = e.target.closest('[data-cal-nav]');
            if (navBtn) {
                state.calMonth += parseInt(navBtn.dataset.calNav);
                if (state.calMonth > 11) { state.calMonth = 0;  state.calYear++; }
                if (state.calMonth < 0)  { state.calMonth = 11; state.calYear--; }
                buildMonthGrids(calEl);
            }
        });

        body.appendChild(infoEl);
        body.appendChild(calEl);
        body.appendChild(dockedNav);

        root.classList.add('is-step-calendar');
        setupCalendarFloat(dockedNav);
        syncCalendarFloat(hasSelection);
    }

    function updateCalendarUI(calEl, infoEl, dockedNextBtn) {
        refreshInfo(infoEl);
        const locked = !(state.dateFrom && state.dateTo);

        if (!locked) {
            clearTimeout(flashBtnHint._t);
            dockedNextBtn.classList.remove('is-hint');
            dockedNextBtn.textContent = dockedNextBtn.dataset.defaultLabel || 'Toliau';
        }

        lockBtn(dockedNextBtn, locked);
        syncCalendarFloat(!locked);
        buildMonthGrids(calEl);
    }

    function showCalendarError(infoEl, message) {
        refreshInfo(infoEl);

        if (showCalendarToast(message)) {
            return;
        }

        const err = make('p', 'booking-form__calendar-error');
        err.textContent = message;
        infoEl.appendChild(err);
    }

    function handleDayClick(date, calEl, infoEl, dockedNextBtn) {
        if (date <= siteToday) return;

        const hasSelection = state.dateFrom && state.dateTo;
        const isSingleDay  = hasSelection && state.dateFrom === state.dateTo;
        const isRange      = hasSelection && state.dateFrom !== state.dateTo;

        // Same single-day click — deselect
        if (isSingleDay && date === state.dateFrom) {
            state.dateFrom = null;
            state.dateTo   = null;
            updateCalendarUI(calEl, infoEl, dockedNextBtn);
            return;
        }

        // Range already selected — start a new single-day selection
        if (isRange) {
            state.dateFrom = date;
            state.dateTo   = date;
            updateCalendarUI(calEl, infoEl, dockedNextBtn);
            return;
        }

        // First click — single-day selection
        if (!hasSelection) {
            state.dateFrom = date;
            state.dateTo   = date;
            updateCalendarUI(calEl, infoEl, dockedNextBtn);
            return;
        }

        // Second click — extend to a range
        const from = date < state.dateFrom ? date : state.dateFrom;
        const to   = date < state.dateFrom ? state.dateFrom : date;

        if (rangeHasBlocked(from, to)) {
            showCalendarError(
                infoEl,
                'Pasirinktame laikotarpyje yra užimta data. Pasirinkite kitą laikotarpį.'
            );
            buildMonthGrids(calEl);
            return;
        }

        state.dateFrom = from;
        state.dateTo   = to;
        updateCalendarUI(calEl, infoEl, dockedNextBtn);
    }

    function rangeHasBlocked(from, to) {
        const cur = parseDate(from);
        const end = parseDate(to);
        while (cur <= end) {
            if (state.blockedDates.has(toStr(cur))) return true;
            cur.setDate(cur.getDate() + 1);
        }
        return false;
    }

    function refreshInfo(el) {
        if (!state.dateFrom || !state.dateTo) {
            el.innerHTML = '<p class="booking-form__calendar-hint">Pasirinkite datą arba laikotarpį</p>';
            return;
        }

        const isSingle = state.dateFrom === state.dateTo;
        const dateLabel = isSingle
            ? `Pasirinkta data: <strong>${displayDate(state.dateFrom)}</strong>`
            : `Pasirinktas laikotarpis: <strong>${displayDate(state.dateFrom)} – ${displayDate(state.dateTo)}</strong>`;

        // Build dynamic price preview if products are selected.
        let pricePreviewHtml = '';
        if (state.products.length > 0 && state.selected.size > 0) {
            const pricing = calculateBookingPricing(
                state.products,
                state.selected,
                state.dateFrom,
                state.dateTo,
                { extensionCable: false, generator: false }
            );

            const selectedProducts = state.products.filter(p => state.selected.has(p.id));
            const hasTwoRates = selectedProducts.some(
                p => p.price_weekend && p.price_weekend !== p.price_weekday
            );

            let rowsHtml = '';

            if (hasTwoRates && (pricing.weekdayDays > 0 || pricing.weekendDays > 0)) {
                // Show per-day-type breakdown.
                selectedProducts.forEach(p => {
                    const wdPrice = p.price_weekday || p.price || 0;
                    const wePrice = p.price_weekend || wdPrice;

                    if (pricing.weekdayDays > 0) {
                        const wdTotal = wdPrice * pricing.weekdayDays;
                        rowsHtml += `
                            <div class="booking-form__price-preview-row">
                                <span>${sanitizeText(p.name)} <em>(darbo d.)</em> — ${pricing.weekdayDays} d. × ${formatEuro(wdPrice)}</span>
                                <strong>${formatEuro(wdTotal)}</strong>
                            </div>`;
                    }
                    if (pricing.weekendDays > 0) {
                        const weTotal = wePrice * pricing.weekendDays;
                        rowsHtml += `
                            <div class="booking-form__price-preview-row">
                                <span>${sanitizeText(p.name)} <em>(savaitg.)</em> — ${pricing.weekendDays} d. × ${formatEuro(wePrice)}</span>
                                <strong>${formatEuro(weTotal)}</strong>
                            </div>`;
                    }
                });
            } else {
                // Single rate — simple summary.
                selectedProducts.forEach(p => {
                    const dayPrice = p.price_weekday || p.price || 0;
                    const total    = dayPrice * pricing.days;
                    const label    = pricing.days > 1
                        ? `${sanitizeText(p.name)} — ${pricing.days} d. × ${formatEuro(dayPrice)}`
                        : sanitizeText(p.name);
                    rowsHtml += `
                        <div class="booking-form__price-preview-row">
                            <span>${label}</span>
                            <strong>${formatEuro(total)}</strong>
                        </div>`;
                });
            }

            pricePreviewHtml = `
                <div class="booking-form__price-preview">
                    ${rowsHtml}
                    <div class="booking-form__price-preview-total">
                        <span>Preliminari suma</span>
                        <strong>${formatEuro(pricing.productsTotal)}</strong>
                    </div>
                </div>`;
        }

        el.innerHTML = `
            <p class="booking-form__calendar-selected">${dateLabel}</p>
            ${pricePreviewHtml}`;
    }

    function buildMonthGrids(container) {
        container.innerHTML = '';

        // Two consecutive months
        const m1 = { y: state.calYear, m: state.calMonth };
        const m2 = state.calMonth === 11
            ? { y: state.calYear + 1, m: 0 }
            : { y: state.calYear,     m: state.calMonth + 1 };

        // Header with prev/next controls
        const isMinMo  = state.calYear === siteTodayDate.getFullYear() && state.calMonth === siteTodayDate.getMonth();
        const header   = make('div', 'booking-form__cal-header');
        header.innerHTML = `
            <button class="booking-form__cal-nav" data-cal-nav="-1" type="button"
                    aria-label="Praeitas mėnuo" ${isMinMo ? 'disabled' : ''}>‹</button>
            <span>${MONTHS[m1.m]} ${m1.y} — ${MONTHS[m2.m]} ${m2.y}</span>
            <button class="booking-form__cal-nav" data-cal-nav="1" type="button"
                    aria-label="Kitas mėnuo">›</button>
        `;
        container.appendChild(header);

        const monthsWrap = make('div', 'booking-form__months');
        monthsWrap.appendChild(buildMonth(m1.y, m1.m));
        monthsWrap.appendChild(buildMonth(m2.y, m2.m));
        container.appendChild(monthsWrap);
    }

    function buildMonth(year, month) {
        const wrap = make('div', 'booking-form__month');
        wrap.appendChild(make('div', 'booking-form__month-name', `${MONTHS[month]} ${year}`));

        const grid   = make('div', 'booking-form__month-grid');

        // Weekday headers
        WEEK_DAYS.forEach(d => grid.appendChild(make('div', 'booking-form__weekday', d)));

        const firstDay = new Date(year, month, 1);
        const lastDay  = new Date(year, month + 1, 0);
        const offset   = (firstDay.getDay() + 6) % 7; // Mon = 0

        // Empty leading cells
        for (let i = 0; i < offset; i++) {
            grid.appendChild(make('div', 'booking-form__day booking-form__day--empty'));
        }

        for (let d = 1; d <= lastDay.getDate(); d++) {
            const dateStr  = toStr(new Date(year, month, d));
            const isPast   = dateStr <= siteToday;
            const isBlocked = state.blockedDates.has(dateStr);
            const isFrom   = state.dateFrom === dateStr;
            const isTo     = state.dateTo   === dateStr;
            const inSelection = state.dateFrom && state.dateTo &&
                dateStr >= state.dateFrom && dateStr <= state.dateTo;

            const btn = document.createElement('button');
            btn.type        = 'button';
            btn.className   = 'booking-form__day';
            btn.textContent = d;
            btn.dataset.date = dateStr;
            btn.setAttribute('aria-label', dateStr);

            if (isPast || isBlocked) btn.disabled = true;
            if (isPast)              btn.classList.add('is-past', 'is-disabled');
            if (isBlocked)          btn.classList.add('is-blocked', 'is-disabled');
            if (inSelection)        btn.classList.add('is-selected');
            if (isFrom)             btn.classList.add('is-from');
            if (isTo)               btn.classList.add('is-to');

            grid.appendChild(btn);
        }

        wrap.appendChild(grid);
        return wrap;
    }

    // ── Step 3: Customer details ──────────────────────────────────────────────

    function renderCustomer() {
        body.innerHTML = '';

        const selectedProducts = state.products.filter(p => state.selected.has(p.id));
        const productNames     = selectedProducts.map(p => sanitizeText(p.name)).join(', ');

        const summary = make('div', 'booking-form__summary');
        summary.innerHTML = `
            <div class="booking-form__summary-item">
                <span>Produktai:</span><strong data-summary="products">${productNames}</strong>
            </div>
            <div class="booking-form__summary-item">
                <span>Datos:</span>
                <strong data-summary="dates">${sanitizeText(formatDateRange(state.dateFrom, state.dateTo))}</strong>
            </div>
            <div class="booking-form__summary-item booking-form__summary-item--addons" data-summary="addons-wrap" hidden>
                <span>Papildomos paslaugos:</span><strong data-summary="addons"></strong>
            </div>
            <div class="booking-form__summary-total">
                <span>Preliminari suma:</span>
                <strong data-summary="total"></strong>
            </div>
        `;

        const form = document.createElement('form');
        form.className = 'booking-form__customer';
        form.noValidate = true;
        form.innerHTML = `
            <div class="booking-form__field">
                <label class="booking-form__label" for="bf-name">
                    Vardas Pavardė <abbr title="privaloma">*</abbr>
                </label>
                <input class="booking-form__input" type="text" id="bf-name"
                       name="customer_name" required autocomplete="name"
                       placeholder="Jonas Jonaitis">
            </div>
            <div class="booking-form__field">
                <label class="booking-form__label" for="bf-phone">
                    Telefono numeris <abbr title="privaloma">*</abbr>
                </label>
                <input class="booking-form__input" type="tel" id="bf-phone"
                       name="customer_phone" required autocomplete="tel"
                       placeholder="+370 600 00000">
            </div>
            <div class="booking-form__field">
                <label class="booking-form__label" for="bf-email">
                    El. paštas <abbr title="privaloma">*</abbr>
                </label>
                <input class="booking-form__input" type="email" id="bf-email"
                       name="customer_email" required autocomplete="email"
                       placeholder="jonas@example.com">
            </div>
            <div class="booking-form__field">
                <label class="booking-form__label" for="bf-address">
                    Renginio adresas <abbr title="privaloma">*</abbr>
                </label>
                <input class="booking-form__input" type="text" id="bf-address"
                       name="event_address" required autocomplete="street-address"
                       placeholder="Įveskite šventės adresą">
            </div>

            <fieldset class="booking-form__addons">
                <legend class="booking-form__addons-title">Papildomos paslaugos</legend>

                <label class="booking-form__addon">
                    <input type="checkbox" name="needs_extension_cable" id="bf-extension" value="1">
                    <span class="booking-form__addon-switch" aria-hidden="true"></span>
                    <span class="booking-form__addon-text">
                        <span class="booking-form__addon-label">Reikia prailgintuvo</span>
                        <span class="booking-form__addon-price">+${ADDON_PRICES.extension_cable} €</span>
                    </span>
                </label>

                <label class="booking-form__addon">
                    <input type="checkbox" name="needs_generator" id="bf-generator" value="1">
                    <span class="booking-form__addon-switch" aria-hidden="true"></span>
                    <span class="booking-form__addon-text">
                        <span class="booking-form__addon-label">Reikia generatoriaus</span>
                        <span class="booking-form__addon-price">+${ADDON_PRICES.generator} €</span>
                    </span>
                </label>
            </fieldset>

            <div class="booking-form__field">
                <label class="booking-form__label" for="bf-notes">Pastabos</label>
                <textarea class="booking-form__input booking-form__textarea"
                          id="bf-notes" name="notes" rows="3"
                          placeholder="Papildoma informacija, klausimai…"></textarea>
            </div>

            <div class="booking-form__field booking-form__field--invoice">
                <label class="booking-form__consent-label">
                    <input type="checkbox" name="invoice_required" id="bf-invoice" value="1">
                    <span>Reikalinga sąskaita faktūra</span>
                </label>
            </div>

            <div class="booking-form__invoice-fields" id="bf-invoice-fields" hidden>
                <div class="booking-form__field">
                    <label class="booking-form__label" for="bf-invoice-name">
                        Įmonės pavadinimas <abbr title="privaloma">*</abbr>
                    </label>
                    <input class="booking-form__input" type="text" id="bf-invoice-name"
                           name="invoice_company_name" autocomplete="organization"
                           placeholder="UAB Pavyzdys">
                </div>
                <div class="booking-form__field">
                    <label class="booking-form__label" for="bf-invoice-code">
                        Įmonės kodas <abbr title="privaloma">*</abbr>
                    </label>
                    <input class="booking-form__input" type="text" id="bf-invoice-code"
                           name="invoice_company_code" placeholder="123456789">
                </div>
                <div class="booking-form__field">
                    <label class="booking-form__label" for="bf-invoice-vat">PVM kodas</label>
                    <input class="booking-form__input" type="text" id="bf-invoice-vat"
                           name="invoice_vat_code" placeholder="LT123456789">
                </div>
                <div class="booking-form__field">
                    <label class="booking-form__label" for="bf-invoice-address">
                        Įmonės adresas <abbr title="privaloma">*</abbr>
                    </label>
                    <input class="booking-form__input" type="text" id="bf-invoice-address"
                           name="invoice_company_address" autocomplete="street-address"
                           placeholder="Gatvė, miestas">
                </div>
            </div>

            <div class="booking-form__field booking-form__field--consent">
                <label class="booking-form__consent-label">
                    <input type="checkbox" name="consent" id="bf-consent" required>
                    <span>Sutinku, kad mano asmens duomenys (vardas, telefono numeris, el. paštas)
                    būtų naudojami rezervacijos tvarkymui pagal
                    <a href="${sanitizeText(privacyUrl)}" target="_blank" rel="noopener noreferrer">Privatumo politiką</a>.
                    <abbr title="privaloma">*</abbr></span>
                </label>
            </div>
        `;

        const getAddons = () => ({
            extensionCable: form.querySelector('#bf-extension').checked,
            generator:      form.querySelector('#bf-generator').checked,
        });

        const invoiceCheckbox = form.querySelector('#bf-invoice');
        const invoiceFields   = form.querySelector('#bf-invoice-fields');
        const invoiceInputs   = invoiceFields.querySelectorAll('input');

        const syncInvoiceFields = () => {
            const show = invoiceCheckbox.checked;
            invoiceFields.hidden = !show;
            invoiceInputs.forEach((input) => {
                if (show && input.name !== 'invoice_vat_code') {
                    input.required = true;
                } else {
                    input.required = false;
                }
            });
        };

        invoiceCheckbox.addEventListener('change', syncInvoiceFields);
        syncInvoiceFields();
        bindCustomerFormValidation(form);

        const updateSummary = () => {
            const pricing = calculateBookingPricing(
                state.products,
                state.selected,
                state.dateFrom,
                state.dateTo,
                getAddons()
            );

            summary.querySelector('[data-summary="total"]').textContent = formatEuro(pricing.total);

            const addonParts = [];
            if (getAddons().extensionCable) addonParts.push(`Prailgintuvas (+${ADDON_PRICES.extension_cable} €)`);
            if (getAddons().generator)      addonParts.push(`Generatorius (+${ADDON_PRICES.generator} €)`);

            const addonsWrap = summary.querySelector('[data-summary="addons-wrap"]');
            const addonsEl   = summary.querySelector('[data-summary="addons"]');

            if (addonParts.length) {
                addonsEl.textContent = addonParts.join(', ');
                addonsWrap.hidden = false;
            } else {
                addonsEl.textContent = '';
                addonsWrap.hidden = true;
            }
        };

        form.querySelectorAll('.booking-form__addon input[type="checkbox"]').forEach(input => {
            input.addEventListener('change', updateSummary);
        });

        updateSummary();

        const errorEl = make('div', 'booking-form__submit-error');
        errorEl.style.display = 'none';

        const nav     = makeNav({ back: true, nextLabel: 'Rezervuoti' });
        const nextBtn = nav.querySelector('.booking-form__btn-next');

        nav.querySelector('.booking-form__btn-back').addEventListener('click', () => goToStep(2));

        nextBtn.addEventListener('click', async () => {
            errorEl.style.display = 'none';
            syncInvoiceFields();

            const validation = validateCustomerForm(form);
            if (!validation.valid) {
                validation.firstInvalid?.focus({ preventScroll: true });
                validation.firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }

            nextBtn.disabled    = true;
            nextBtn.textContent = 'Siunčiama…';

            const fd = new FormData(form);
            const invoiceRequired = fd.get('invoice_required') === '1';

            try {
                const result = await apiFetch(`${apiBase}/bookings`, {
                    method:  'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce':   nonce,
                    },
                    body: JSON.stringify({
                        product_ids:           [...state.selected],
                        date_from:             state.dateFrom,
                        date_to:               state.dateTo,
                        customer_name:         fd.get('customer_name'),
                        customer_phone:        fd.get('customer_phone'),
                        customer_email:        fd.get('customer_email'),
                        event_address:         fd.get('event_address'),
                        needs_extension_cable: fd.get('needs_extension_cable') === '1',
                        needs_generator:       fd.get('needs_generator') === '1',
                        notes:                 fd.get('notes') || '',
                        consent:               true,
                        invoice_required:          invoiceRequired,
                        invoice_company_name:      invoiceRequired ? fd.get('invoice_company_name') : '',
                        invoice_company_code:      invoiceRequired ? fd.get('invoice_company_code') : '',
                        invoice_vat_code:          invoiceRequired ? (fd.get('invoice_vat_code') || '') : '',
                        invoice_company_address:   invoiceRequired ? fd.get('invoice_company_address') : '',
                    }),
                });
                renderSuccess(result.order_number || '');
            } catch (err) {
                nextBtn.disabled    = false;
                nextBtn.textContent = 'Rezervuoti';
                errorEl.textContent  = err.message || 'Klaida. Bandykite dar kartą.';
                errorEl.style.display = '';
            }
        });

        body.appendChild(summary);
        body.appendChild(form);
        body.appendChild(errorEl);
        body.appendChild(nav);
    }

    // ── Success ───────────────────────────────────────────────────────────────

    function renderSuccess(orderNumber = '') {
        stepsEl.style.display = 'none';
        body.classList.add('is-success');
        root.closest('.booking-form-section')?.classList.add('is-success');

        const orderLine = orderNumber
            ? `<p class="booking-form__success-order"><span class="booking-form__success-order-label">Užsakymo numeris</span><span class="booking-form__success-order-value">${sanitizeText(orderNumber)}</span></p>`
            : '';

        body.innerHTML = `
            <div class="booking-form__success">
                <div class="booking-form__success-icon">✓</div>
                <h3 class="booking-form__success-title">Jūsų rezervacija sėkmingai pateikta.</h3>
                ${orderLine}
                <p class="booking-form__success-sub">
                    Netrukus el. paštu gausite rezervacijos informaciją ir apmokėjimo rekvizitus.
                    Kad data būtų galutinai rezervuota, avansą prašome apmokėti per 2 darbo dienas.
                </p>
                <button class="btn btn--secondary booking-form__restart" type="button">
                    Rezervuoti dar kartą
                </button>
            </div>
        `;

        body.querySelector('.booking-form__restart').addEventListener('click', () => {
            state.selected.clear();
            state.blockedDates.clear();
            state.dateFrom  = null;
            state.dateTo    = null;
            state.calYear   = new Date().getFullYear();
            state.calMonth  = new Date().getMonth();
            stepsEl.style.display = '';
            body.classList.remove('is-success');
            root.closest('.booking-form-section')?.classList.remove('is-success');
            goToStep(1);
        });
    }

    goToStep(1);
}
